/**
 * Title-slide media. Assets on S3 (not shipped in the static export).
 *
 * Mosaic stills are WebP @ 1280 long-edge (≈2× a ⅓-viewport cell). Re-encode:
 *   cwebp -q 72 -m 6 -af -metadata none <scaled.jpg> -o <name>.webp
 * Desktop loops: muted H.264 ~1Mbps (`+faststart`). Mobile: 720 long-edge
 * ~700kbps baseline. Posters are WebP too.
 *
 * Re-sync local `assets/title/` with:
 *   aws --profile voxmedia s3 sync \
 *     apps/duet-csk-theverge/src/stories/data-centers-hub/assets/title/ \
 *     s3://assets.sbnation.com/csk/uploads/verge-features/data-centers/data-centers-hub/title/ \
 *     --acl public-read --exclude "*" --include "*.webp" --include "*.mp4" --include "*.svg" \
 *     --exclude "*.orig.*"
 */

import { hubS3Asset } from "@story/data-centers-hub/config/hubS3";

/** Bust browser/S3 caches after replacing files at the same keys. */
const V = "20261026b";

export function hubTitleAsset(path: string): string {
  return hubS3Asset("title", path, V);
}

export type TitleMediaStill = {
  kind: "image";
  src: string;
};

export type TitleMediaVideo = {
  kind: "video";
  src: string;
  poster: string;
  /** Narrow / phone cut — lower res + bitrate for concurrent mosaic decode. */
  srcMobile?: string;
  posterMobile?: string;
};

export type TitleMedia = TitleMediaStill | TitleMediaVideo;

/** Desktop vs narrow src/poster for a video cell. */
export function resolveVideoUrls(
  media: TitleMediaVideo,
  narrow: boolean,
): { src: string; poster: string } {
  if (narrow && media.srcMobile) {
    return {
      src: media.srcMobile,
      poster: media.posterMobile ?? media.poster,
    };
  }
  return { src: media.src, poster: media.poster };
}

/**
 * Freeze a video cell to its poster. Narrow slides keep one loop (the main
 * cell) and pass secondary videos through this so iOS isn't juggling several
 * decoders at once.
 */
export function asStill(media: TitleMedia, narrow = true): TitleMedia {
  if (media.kind === "image") {
    return media;
  }
  return {
    kind: "image",
    src: narrow ? (media.posterMobile ?? media.poster) : media.poster,
  };
}

/** Cell media map for the settled title slide. */
export const titleSlideMedia = {
  tlTop: {
    kind: "image",
    src: hubTitleAsset("tl-cooling.webp"),
  },
  tlBottom: {
    kind: "image",
    src: hubTitleAsset("tl-flowers.webp"),
  },
  tm: {
    kind: "image",
    src: hubTitleAsset("tm-soccer.webp"),
  },
  tr: {
    kind: "video",
    src: hubTitleAsset("tr-field.mp4"),
    poster: hubTitleAsset("tr-field-poster.webp"),
    srcMobile: hubTitleAsset("tr-field-mobile.mp4"),
    posterMobile: hubTitleAsset("tr-field-mobile-poster.webp"),
  },
  blMerged: {
    kind: "video",
    src: hubTitleAsset("bl-suburb.mp4"),
    poster: hubTitleAsset("bl-suburb-poster.webp"),
    srcMobile: hubTitleAsset("bl-suburb-mobile.mp4"),
    posterMobile: hubTitleAsset("bl-suburb-mobile-poster.webp"),
  },
  brTop: {
    kind: "video",
    src: hubTitleAsset("br-az.mp4"),
    poster: hubTitleAsset("br-az-poster.webp"),
    srcMobile: hubTitleAsset("br-az-mobile.mp4"),
    posterMobile: hubTitleAsset("br-az-mobile-poster.webp"),
  },
  brBottom: {
    kind: "image",
    src: hubTitleAsset("br-protest.webp"),
  },
  hed: hubTitleAsset("hed.svg"),
  /** Narrow lockup — stacked lines for the mobile mosaic. */
  hedMobile: hubTitleAsset("hed-mobile.svg"),
  dek: "An investigative series about America's data centers: past, present, and future.",
} as const satisfies {
  tlTop: TitleMedia;
  tlBottom: TitleMedia;
  tm: TitleMedia;
  tr: TitleMediaVideo;
  blMerged: TitleMediaVideo;
  brTop: TitleMediaVideo;
  brBottom: TitleMedia;
  hed: string;
  hedMobile: string;
  dek: string;
};
